/**
 * `tools.<name>` needs one entry per backend tool (spec section 12.2).
 * `screens.<id>` needs one entry per route in `AI_SCREEN_CONTEXT_ROUTES` (spec section 12.5).
 * @see [12. How to extend](../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
export default {
  roleYou: 'You',
  roleAssistant: 'Assistant',
  loading: 'Loading messages…',
  loadFailed: 'The messages could not be loaded.',
  empty: 'No messages in this chat yet.',
  emptyNewChat: 'Write a message to start a new chat.',
  sendFailed: 'The message failed: {{message}}',
  showReasoning: 'Thought',
  hideReasoning: 'Hide reasoning',
  showDetails: 'Details',
  hideDetails: 'Hide details',
  reasoningLive: 'Thinking…',
  toolUnnamed: 'Tool call',
  toolRunning: 'Running…',
  toolDone: 'Done',
  toolFailed: 'Failed',
  tools: {
    lookUpDocument: 'Search documents',
    documentsFound_one: 'Document found',
    documentsFound_other: '{{count}} documents found',
    writeDocument: 'Write document',
    shareDocument: 'Share document',
    documentShared: 'Shared',
    documentNotShared: 'Not shared',
    listValueChains: 'List value chains',
    valueChainsListed_one: '{{count}} value chain',
    valueChainsListed_other: '{{count}} value chains',
    lookupStandards: 'Look up standards',
    standardsListed_one: '{{name}} · {{count}} topic',
    standardsListed_other: '{{name}} · {{count}} topics',
    standardsLookupEmpty: 'No topics',
    lookupPublicCompany: 'Look up public company',
    lookupDossierPublicFacts: 'Look up dossier facts',
    publicCompanyLookedUp: 'Public facts',
    publicCompanyLookupMiss: 'No public facts',
    publicCompaniesLookedUp_one: '{{count}} public company',
    publicCompaniesLookedUp_other: '{{count}} public companies',
    searchWatchCompanies: 'Search watch companies',
    watchCompaniesFound_one: '{{count}} registry hit',
    watchCompaniesFound_other: '{{count}} registry hits',
    createWatchCompanies: 'Create watch companies',
    watchCompaniesCreated_one: 'Created {{count}} company',
    watchCompaniesCreated_other: 'Created {{count}} companies',
    watchCompaniesCreateInvalid: 'Seed is not complete',
    fetchData: 'Fetch data',
    dataRowsFetched_one: '{{count}} row',
    dataRowsFetched_other: '{{count}} rows',
    dataRowsStored: 'Rows stored: {{count}}',
    dataRemainingRows: 'Rows after the sample: {{count}}',
    dataClarification: 'Needs clarification',
    dataRejected: 'Rejected',
    createDatasource: 'Store data',
    datasourceCreated_one: '{{count}} row',
    datasourceCreated_other: '{{count}} rows',
    datasourceNotCreated: 'Not stored',
    datasourceColumns: 'Columns: {{columns}}',
    showDatasource: 'Show data',
    datasourceRows_one: '{{count}} row',
    datasourceRows_other: '{{count}} rows',
    datasourceTruncated: 'Showing the first {{count}} rows. The query had more.',
    datasourceLoading: 'Loading the rows…',
    datasourceLoadFailed: 'The rows could not be loaded.',
    datasourceEmpty: 'No rows.',
    datasourceDropped: 'The rows were dropped and cannot be made again.',
    datasourceAsOf: 'Data as of {{- date}}',
    datasourceYes: 'Yes',
    datasourceNo: 'No',
    showChart: 'Show chart',
    displayFailed: 'Failed to display {{type}}',
    displayTable: 'table',
    displayChart: 'chart',
    displayLineChart: 'line chart',
    displayPieChart: 'pie chart',
    chartShowTable: 'Table',
    chartShowChart: 'Chart',
    chartEmpty: 'Nothing to chart: no row has a number in the value column.',
    chartSeriesDropped: 'Showing {{shown}} of {{total}} series. Open the table to read the rest.',
    chartSlicesFolded_one: '{{count}} smaller row is folded into "{{other}}".',
    chartSlicesFolded_other: '{{count}} smaller rows are folded into "{{other}}".',
    chartOther: 'Other',
    createImpactAnalysis: 'Create impact analysis',
    updateImpactAnalysis: 'Update impact analysis',
    startImpactAnalysis: 'Start impact analysis',
    impactAnalysisFallbackName: 'analysis {{id}}',
    impactScenarioFallbackName: 'scenario {{index}}',
    impactAnalysisCreated: '{{- name}} - created',
    impactAnalysisUpdated: '{{- name}} - updated',
    impactAnalysisRefused: 'Not done',
    impactUnknownUnits_one: '{{- name}}: {{count}} id not on the map',
    impactUnknownUnits_other: '{{- name}}: {{count}} ids not on the map',
    impactDatasourceProblem: '{{- name}}: {{- problem}}',
    impactRunStarted: '{{- name}} - run started in the panel',
    impactPanelOpened: '{{- name}} - panel opened',
    impactRunCounts: '{{scenarios}}, {{units}}',
    impactScenarioCount_one: '{{count}} scenario',
    impactScenarioCount_other: '{{count}} scenarios',
    impactUnitCount_one: '{{count}} unit',
    impactUnitCount_other: '{{count}} units',
    impactOpenPanel: 'Open in the Impact Scenarios panel',
    impactResultDeleted: 'The result was deleted.',
    impactResultLoadFailed: 'The result could not be loaded.',
    createAgentSession: 'Create agent session',
    updateAgentSessionUnits: 'Update agent session',
    startAgentSearch: 'Start AI search',
    agentSupplierDiscovery: 'Supplier discovery',
    agentBillOfMaterials: 'Bill of materials',
    agentSessionFallbackName: 'session {{id}}',
    agentSessionRefused: 'Not done',
    agentSessionCreated: '{{agent}} - {{tabs}}, created',
    agentSessionUpdated: '{{agent}} - {{tabs}}, updated',
    agentTabCount_one: '{{count}} tab',
    agentTabCount_other: '{{count}} tabs',
    agentSearchStartedAll: 'AI search started in all tabs',
    agentSearchStartedTabs: 'AI search started in {{tabs}}',
    agentSessionOpened: '{{agent}} - session opened',
    agentSearchFailed_one: '{{count}} tab could not start',
    agentSearchFailed_other: '{{count}} tabs could not start',
    agentOpenSession: 'Open session',
    createMonitoringSession: 'Create monitoring session',
    updateMonitoringSessionUnits: 'Update monitoring session',
    startMonitoringSession: 'Start monitoring',
    monitoringAgent: 'Monitoring',
    monitoringSessionCreated: '{{agent}} - {{units}}, created',
    monitoringSessionUpdated: '{{agent}} - {{units}}, updated',
    monitoringProductCount_one: '{{count}} product',
    monitoringProductCount_other: '{{count}} products',
    monitoringSupplierCount_one: '{{count}} supplier',
    monitoringSupplierCount_other: '{{count}} suppliers',
    monitoringEntityCount_one: '{{count}} entity',
    monitoringEntityCount_other: '{{count}} entities',
    monitoringUnitCount_one: '{{count}} unit',
    monitoringUnitCount_other: '{{count}} units',
    monitoringTemplate: 'Template: {{- name}}',
    monitoringUnknownUnits_one: '{{count}} id not on the map',
    monitoringUnknownUnits_other: '{{count}} ids not on the map',
    monitoringNotSelectedUnits_one: '{{count}} id was not selected',
    monitoringNotSelectedUnits_other: '{{count}} ids were not selected',
    monitoringTemplateOptions: 'Templates to pick from:',
    monitoringStarted: 'Monitoring started for {{units}}',
    monitoringOpened: '{{agent}} - results opened',
    monitoringViewResults: 'View results',
  },
  messageLabel: 'Message',
  placeholder: 'Press Enter to send. Shift+Enter for new line',
  send: 'Send',
  sending: 'Sending…',
  panelTitle: 'Ask Attaima',
  openPanel: 'Ask Attaima',
  hidePanel: 'Hide the assistant',
  dockPanel: 'Dock to the side',
  floatPanel: 'Float',
  panelFailed: 'The assistant ran into a problem.',
  tryAgain: 'Try again',
  openHistory: 'Open the chat history',
  backToChat: 'Back to the chat',
  historyLoading: 'Loading the chats…',
  historyFailed: 'The chats could not be loaded.',
  historyEmpty: 'No chats yet. Write a message to start one.',
  newChat: 'New chat',
  chatLabel: 'Chat {{id}}',
  deleteChat: 'Delete the chat',
  deleteChatConfirm: 'Delete this chat? Its messages cannot be read back.',
  deleteChatFailed: 'The chat could not be deleted.',
  contextLabel: 'Context:',
  screens: {
    launchpad: 'Launchpad',
    launchpadHub: 'Value chain launchpad',
    settings: 'Settings',
    releaseHistory: 'Release History',
    userManagement: 'User management',
    uiConfiguration: 'UI configuration',
    balanceSheetAssets: 'Balance sheet assets',
    suppliers: 'Suppliers',
    customers: 'Customers',
    products: 'Products',
    profitCentres: 'Profit centres',
    valueChainMap: 'Value chain maps',
    companyMap: 'Company map',
    taskManagement: 'Task management',
    taskInfo: 'Task',
    materialityAssessment: 'Materiality assessment',
    standards: 'Standards',
    standardsIRO: 'IRO standards',
    sectorSpecific: 'Sector specific',
    agentsManagement: 'Agents management',
    settingWeighting: 'Weighting',
    clients: 'Clients',
    clientProfile: 'Client profile',
    profile: 'Profile',
    watchTower: 'Watch Tower',
    watchTowerCompany: 'Watch company dossier',
    marketingOps: 'Marketing Ops',
    marketingOpsFinding: 'Finding story',
  },
};
